/**
 * SectionErrorBoundary Component
 * 
 * A lightweight error boundary for wrapping specific sections of a page.
 * Provides graceful degradation without breaking the entire page.
 * 
 * @module SectionErrorBoundary
 */

import React from 'react';
import PropTypes from 'prop-types';
import { logger } from '../utils/logger';

class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error(`[SectionErrorBoundary:${this.props.name}] Error caught:`, {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      section: this.props.name
    });
  }

  handleRetry = () => {
    this.setState(prev => ({ 
      hasError: false, 
      error: null,
      retryCount: prev.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback provided, use it
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      // Default inline fallback - minimal and unobtrusive
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <h3 className="font-semibold text-red-800 text-sm mb-1">
                {this.props.title || 'Section Unavailable'}
              </h3>
              <p className="text-red-600 text-xs mb-2">
                {this.props.message || 'This section encountered an error and couldn\'t load.'}
              </p>
              {this.state.retryCount < 3 && (
                <button
                  onClick={this.handleRetry}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

SectionErrorBoundary.propTypes = {
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  fallback: PropTypes.func,
  children: PropTypes.node.isRequired
};

export default SectionErrorBoundary;

